import { useLocation, useNavigate } from "react-router-dom";

const RETURN_STORAGE_KEY = "tc_lead_detail_return";

/** Default when lead detail is opened without return context (bookmark, direct URL). */
export const LEAD_DETAIL_RETURN_DEFAULT = {
  from: "/leads/search",
  fromLabel: "Search Leads",
};

/** Infer label when router state was lost (e.g. MuiLink + RouterLink). */
const FROM_PATH_LABELS = {
  "/settings/action-log": "Action Log",
  "/message-center": "Message Center",
  "/sales-pipeline": "Sales Pipeline",
  "/leads/search": "Search Leads",
  "/dashboard": "Dashboard",
  "/leads": "Map",
  "/follow-up-intelligence": "Follow-Up Intel",
};

function persistLeadDetailReturn(payload) {
  try {
    sessionStorage.setItem(RETURN_STORAGE_KEY, JSON.stringify(payload));
  } catch (_) {
    /* ignore quota / private mode */
  }
}

export function clearLeadDetailReturn() {
  try {
    sessionStorage.removeItem(RETURN_STORAGE_KEY);
  } catch (_) {
    /* ignore */
  }
}

function readPersistedLeadDetailReturn() {
  try {
    const raw = sessionStorage.getItem(RETURN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.from === "string") return parsed;
  } catch (_) {
    /* ignore */
  }
  return null;
}

function labelFromPath(fromPath) {
  if (!fromPath) return null;
  const pathOnly = fromPath.split("?")[0];
  if (FROM_PATH_LABELS[pathOnly]) return FROM_PATH_LABELS[pathOnly];
  if (pathOnly.startsWith("/message-center")) return "Message Center";
  return null;
}

function resolveReturnState(locationState) {
  const persisted = readPersistedLeadDetailReturn();
  const merged = {
    ...(persisted || {}),
    ...(locationState || {}),
  };

  let from =
    typeof merged.from === "string" && merged.from.trim()
      ? merged.from.trim()
      : "";
  let fromLabel =
    typeof merged.fromLabel === "string" && merged.fromLabel.trim()
      ? merged.fromLabel.trim()
      : "";

  if (!fromLabel && from) {
    fromLabel = labelFromPath(from) || "";
  }

  if (!from) {
    from = LEAD_DETAIL_RETURN_DEFAULT.from;
    fromLabel = fromLabel || LEAD_DETAIL_RETURN_DEFAULT.fromLabel;
  } else if (!fromLabel) {
    fromLabel = LEAD_DETAIL_RETURN_DEFAULT.fromLabel;
  }

  return { from, fromLabel, hasReturn: Boolean(merged.from) };
}

/**
 * @param {{ pathname: string, search?: string, label: string }} ctx
 * @returns {{ from: string, fromLabel: string }}
 */
export function buildLeadDetailReturn({ pathname, search = "", label }) {
  const base = (pathname || "").trim() || LEAD_DETAIL_RETURN_DEFAULT.from;
  const from = `${base}${search || ""}`;
  const payload = {
    from,
    fromLabel: label || labelFromPath(from) || LEAD_DETAIL_RETURN_DEFAULT.fromLabel,
  };
  persistLeadDetailReturn(payload);
  return payload;
}

export function navigateToLeadDetail(navigate, leadId, ctx) {
  if (!leadId) return;
  const state = buildLeadDetailReturn(ctx);
  navigate(`/leads/${leadId}`, { state });
}

export function useLeadDetailReturn() {
  const navigate = useNavigate();
  const location = useLocation();
  const { from, fromLabel, hasReturn } = resolveReturnState(location.state);

  const goBack = () => {
    clearLeadDetailReturn();
    if (hasReturn) {
      navigate(from);
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(LEAD_DETAIL_RETURN_DEFAULT.from);
  };

  return {
    from,
    fromLabel,
    goBack,
    hasReturn,
  };
}
